import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Layers, Search, Settings as SettingsIcon } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { employeeMatches, employeeName } from "@/lib/employeeSearch";

const FIELD = "border border-ink/15 py-2 px-3 text-sm bg-white focus:border-klein focus:outline-none";

export default function BulkDivisions() {
    const [employees, setEmployees] = useState([]);
    const [divisions, setDivisions] = useState([]);
    const [dept, setDept] = useState("all");
    const [onlyMissing, setOnlyMissing] = useState(true);
    const [q, setQ] = useState("");
    const [picked, setPicked] = useState(new Set());
    const [target, setTarget] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [e, d] = await Promise.all([api.get("/employees"), api.get("/reference/divisions")]);
            setEmployees((e.data || []).filter((x) => !["resigned", "terminated"].includes(x.status)));
            setDivisions(d.data?.divisions || []);
        } catch {
            toast.error("Could not load staff");
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);
    useEffect(() => { setPicked(new Set()); }, [dept, onlyMissing, q]);

    const depts = useMemo(() => [...new Set(employees.map((e) => e.department).filter(Boolean))].sort(), [employees]);
    const rows = useMemo(() => employees.filter((e) =>
        (dept === "all" || (dept === "__none" ? !e.department : e.department === dept)) &&
        (!onlyMissing || !e.division) && employeeMatches(e, q),
    ), [employees, dept, onlyMissing, q]);

    const allOn = rows.length > 0 && rows.every((r) => picked.has(r.id));
    const toggle = (id) => setPicked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
    const toggleAll = () => setPicked(allOn ? new Set() : new Set(rows.map((r) => r.id)));

    const apply = async () => {
        setSaving(true);
        try {
            const { data } = await api.post("/employees/bulk-division", { employee_ids: [...picked], division: target });
            toast.success(`Division ${target ? `"${target}"` : "cleared"} for ${data.updated} staff`);
            setPicked(new Set());
            window.dispatchEvent(new Event("badges:refresh"));
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-4 sm:p-8 lg:p-12 max-w-6xl" data-testid="bulk-divisions-page">
            <div className="overline">Data clean-up</div>
            <h1 className="mt-2 font-display font-black text-4xl sm:text-5xl tracking-tighter text-ink flex items-center gap-3">
                <Layers className="w-9 h-9 text-klein" /> Set divisions
            </h1>
            <p className="mt-2 text-sm text-ink/60">Tick staff, choose a division, and save them all at once.</p>

            {!loading && divisions.length === 0 && (
                <div className="mt-8 border border-amber-300 bg-amber-50 p-5 text-sm flex flex-wrap items-center gap-3" data-testid="bulk-div-no-list">
                    No divisions set up yet.
                    <Link to="/settings" className="inline-flex items-center gap-1 text-klein underline"><SettingsIcon className="w-4 h-4" /> Add them in Settings</Link>
                </div>
            )}

            <div className="mt-8 flex flex-wrap items-end gap-3 bg-white border border-ink/10 p-4">
                <div className="flex items-center gap-2 border border-ink/15 px-3 flex-1 min-w-[220px]">
                    <Search className="w-4 h-4 text-ink/40" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, staff no, department, phone…" className="w-full py-2 text-sm focus:outline-none" data-testid="bulk-div-search" />
                </div>
                <select value={dept} onChange={(e) => setDept(e.target.value)} className={FIELD} data-testid="bulk-div-dept-filter">
                    <option value="all">All departments</option>
                    <option value="__none">No department</option>
                    {depts.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
                <label className="inline-flex items-center gap-2 text-sm text-ink/70 py-2">
                    <input type="checkbox" checked={onlyMissing} onChange={(e) => setOnlyMissing(e.target.checked)} data-testid="bulk-div-only-missing" />
                    No division only
                </label>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-3 bg-bone/60 border border-ink/10 p-4 sticky top-0 z-10" data-testid="bulk-div-action-bar">
                <span className="text-sm text-ink/70" data-testid="bulk-div-selected-count">{picked.size} selected</span>
                <select value={target} onChange={(e) => setTarget(e.target.value)} className={FIELD} data-testid="bulk-div-target">
                    <option value="">— No division (clear) —</option>
                    {divisions.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
                <button onClick={apply} disabled={!picked.size || saving} className="bg-klein text-white px-5 py-2.5 text-sm hover:bg-klein/90 disabled:opacity-50" data-testid="bulk-div-apply">
                    {saving ? "Saving…" : `Apply to ${picked.size || ""} staff`}
                </button>
            </div>

            <div className="mt-3 bg-white border border-ink/10 overflow-x-auto">
                <table className="w-full text-sm min-w-[640px]" data-testid="bulk-div-table">
                    <thead>
                        <tr className="border-b border-ink/10 bg-bone/50">
                            <th className="px-3 py-3 w-10"><input type="checkbox" checked={allOn} onChange={toggleAll} data-testid="bulk-div-select-all" /></th>
                            {["Emp No", "Name", "Department", "Division"].map((h) => <th key={h} className="text-left overline px-3 py-3">{h}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={5} className="px-4 py-10 text-center text-ink/40">Loading…</td></tr>}
                        {!loading && rows.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-ink/40" data-testid="bulk-div-empty">No staff match these filters</td></tr>}
                        {!loading && rows.map((r) => (
                            <tr key={r.id} onClick={() => toggle(r.id)} className={`border-b border-ink/5 cursor-pointer hover:bg-ink/5 ${picked.has(r.id) ? "bg-klein/5" : ""}`} data-testid={`bulk-div-row-${r.id}`}>
                                <td className="px-3 py-3"><input type="checkbox" checked={picked.has(r.id)} onChange={() => toggle(r.id)} onClick={(e) => e.stopPropagation()} data-testid={`bulk-div-check-${r.id}`} /></td>
                                <td className="px-3 py-3 tabular-nums text-ink/70">{r.employee_no || "—"}</td>
                                <td className="px-3 py-3">{employeeName(r)}</td>
                                <td className="px-3 py-3 text-ink/60">{r.department || "—"}</td>
                                <td className="px-3 py-3">{r.division || <span className="text-amber-700">None</span>}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
